import React from "react";
import { ExternalLink, Github, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { animated } from "@react-spring/web";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import { useTilt } from "../hooks/useTilt";
import SectionParallax from "./SectionParallax";

const sectionVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 140, damping: 18 },
  },
};

const cardContentVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.08 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 180, damping: 16 },
  },
};

const tagVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

gsap.registerPlugin(ScrollTrigger);

const Projects = () => {
  const [projectList, setProjectList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeProject, setActiveProject] = React.useState(null);
  const { projects, projectsSection } = useLocalizedContent();
  const contentShift = useParallax(0.018);
  const gridRef = React.useRef(null);

  const openPreview = (project) => setActiveProject(project);
  const closePreview = () => setActiveProject(null);

  const previewMeta = React.useMemo(() => {
    if (!activeProject) return null;
    const techList =
      activeProject.tech && activeProject.tech.length > 0
        ? activeProject.tech.slice(0, 3).join(", ")
        : "teknologi modern";
    return {
      techList,
      narrative: `Karya di bidang ${activeProject.category} dengan fokus ${activeProject.subtitle}. Dibangun dengan ${techList}.`,
    };
  }, [activeProject]);

  React.useEffect(() => {
    let active = true;

    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const data = await new Promise((resolve) =>
          setTimeout(() => resolve(projects), 400)
        );
        if (active) {
          setProjectList(data);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchProjects();
    return () => {
      active = false;
    };
  }, [projects]);

  React.useLayoutEffect(() => {
    if (isLoading || !gridRef.current) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray(".project-card");
      if (!cards.length) return;

      gsap.fromTo(
        cards,
        { opacity: 0, y: 60, rotateX: 12, transformPerspective: 1000 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 1.4,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        },
      );
    }, gridRef);

    return () => ctx.revert();
  }, [isLoading, projectList.length]);

  React.useEffect(() => {
    if (!activeProject) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [activeProject]);

  React.useEffect(() => {
    if (!activeProject) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveProject(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeProject]);

  return (
    <motion.section
      id="projects"
      className="section-fade relative pt-24 pb-36 md:pb-40 overflow-hidden"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-blue-900/10 via-transparent to-transparent"></div>
      <SectionParallax variant="amber" />

      <div
        className="relative z-10 container mx-auto px-6 lg:px-12 transition-transform duration-500"
        style={{ transform: `translate3d(0, ${contentShift}px, 0)` }}
      >
        <motion.div variants={sectionVariants}>
          {/* Section Header */}
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase bg-blue-500/10 text-blue-300 border border-blue-500/20 mb-4">
              {projectsSection.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {projectsSection.heading}
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              {projectsSection.description}
            </p>
          </motion.div>

          {/* Projects Grid */}
          <div
            ref={gridRef}
            className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8"
          >
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <ProjectCardSkeleton
                    key={`skeleton-${index}`}
                    copy={projectsSection}
                  />
                ))
              : projectList.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    copy={projectsSection}
                    onPreview={openPreview}
                  />
                ))}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {activeProject && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center px-6 py-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={closePreview}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            ></motion.div>
            <motion.div
              className="relative z-10 w-full max-w-5xl"
              role="dialog"
              aria-modal="true"
              aria-label={`${activeProject.title} preview`}
              initial={{ y: 30, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 180, damping: 20 }}
            >
              <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-white/5 via-black/10 to-white/5 p-6 sm:p-8 shadow-[0_35px_120px_rgba(3,7,18,0.75)]">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_55%)]"></div>
                <div className="pointer-events-none absolute -top-24 right-0 h-48 w-48 rounded-full bg-cyan-500/10 blur-3xl"></div>

                <div className="relative z-10">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.5em] text-white/40">
                        Sorotan Portofolio
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge className="px-3 py-1 rounded-full text-[11px] font-medium uppercase bg-white/10 text-white border border-white/15">
                          {activeProject.category}
                        </Badge>
                        <span className="text-[11px] uppercase tracking-[0.3em] text-white/30">
                          Proyek Pilihan
                        </span>
                      </div>
                      <h3 className="mt-3 text-2xl md:text-3xl font-semibold text-white">
                        {activeProject.title}
                      </h3>
                      <p className="text-white/55 text-sm">
                        {activeProject.subtitle}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closePreview}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 transition"
                    >
                      <X className="h-4 w-4" />
                      Tutup
                    </button>
                  </div>

                  <motion.div
                    className="relative mt-8 flex justify-center"
                    animate={{ y: [0, -6, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="pointer-events-none absolute -top-10 left-1/2 flex flex-col items-center -translate-x-1/2">
                      <div className="h-2 w-2 rounded-full bg-white/60 shadow-[0_0_16px_rgba(255,255,255,0.55)]"></div>
                      <div className="h-8 w-px bg-gradient-to-b from-white/50 to-transparent"></div>
                      <div className="h-2 w-16 rounded-full bg-white/15 shadow-[0_0_24px_rgba(255,255,255,0.15)]"></div>
                    </div>
                    <motion.div
                      className="relative w-full max-w-3xl"
                      style={{ transformOrigin: "top center" }}
                      animate={{ rotate: [0, -1.6, 0, 1.6, 0] }}
                      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <div className="pointer-events-none absolute -inset-8 rounded-[32px] bg-gradient-to-b from-blue-500/25 via-cyan-500/10 to-transparent blur-2xl"></div>
                      <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-black/30 shadow-[0_25px_70px_rgba(15,23,42,0.55)]">
                        <img
                          src={activeProject.image}
                          alt={activeProject.title}
                          className="w-full max-h-[70vh] object-contain bg-black/20"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-tr from-white/20 via-transparent to-transparent opacity-70"
                          animate={{ x: ["-20%", "20%"] }}
                          transition={{
                            duration: 6,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        ></motion.div>
                        <div className="absolute -inset-[1px] rounded-[30px] border border-white/10 pointer-events-none"></div>
                      </div>
                    </motion.div>
                  </motion.div>

                  <div className="mt-8 grid gap-6 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                    <div className="space-y-3">
                      <p className="text-xs uppercase tracking-[0.3em] text-white/40">
                        Narasi Singkat
                      </p>
                      <p className="text-white/70 text-sm leading-relaxed">
                        {previewMeta?.narrative}
                      </p>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {activeProject.description}
                      </p>
                    </div>
                    <div className="space-y-3">
                      <p className="text-xs uppercase tracking-[0.3em] text-white/40">
                        Teknologi Kunci
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {activeProject.tech.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-white/70"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      {activeProject.liveUrl && (
                        <a
                          href={activeProject.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-cyan-200/80 hover:text-cyan-100 transition"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Kunjungi demo live
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

const ProjectCard = ({ project, copy, onPreview }) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const tilt = useTilt({ max: 12, scale: 1.03 });
  const buttonBaseClass =
    "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-blue-500/20 hover:border-blue-500/50 text-white/70 hover:text-white transition-all duration-300";
  const disabledClass = "opacity-40 cursor-not-allowed pointer-events-none";

  return (
    <animated.div
      ref={tilt.ref}
      style={tilt.style}
      {...tilt.bind}
      className="project-card will-change-transform"
    >
      <Card className="group relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-blue-500/50 transition-all duration-500">
        <animated.div
          className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-br from-blue-500/20 via-transparent to-cyan-500/30"
          style={{ opacity: tilt.glow.to((value) => value * 0.6) }}
        ></animated.div>

        <motion.div
          className="relative"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.button
            type="button"
            className="relative block h-64 w-full overflow-hidden text-left bg-transparent border-0 p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60"
            variants={cardItemVariants}
            onClick={() => onPreview?.(project)}
            whileTap={{ scale: 0.99 }}
            aria-label={`Open preview for ${project.title}`}
            title={`Open preview for ${project.title}`}
          >
            <motion.img
              src={project.image}
              alt={project.title}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              className={`w-full h-full object-cover transition-all duration-500 transform group-hover:scale-110 ${
                imageLoaded && !imageError ? "opacity-100" : "opacity-0"
              }`}
            />

            <div
              className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-60 group-hover:opacity-40 transition-opacity duration-300 pointer-events-none`}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>

            {!imageLoaded && !imageError && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur-md">
                <Skeleton className="w-16 h-16 rounded-full bg-white/10" />
                <span className="text-xs uppercase tracking-wide text-white/60">
                  {copy.loadingPreview}
                </span>
              </div>
            )}

            {imageError && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-black/60">
                <span className="text-sm font-medium text-white/80">
                  {copy.previewUnavailable}
                </span>
                <span className="text-xs text-white/50">
                  {copy.assetAttention}
                </span>
              </div>
            )}

            <motion.div className="absolute top-4 left-4 z-30" variants={cardItemVariants}>
              <Badge className="px-3 py-1 rounded-full text-xs font-medium uppercase bg-black/60 backdrop-blur-md text-white border border-white/20">
                {project.category}
              </Badge>
            </motion.div>
          </motion.button>

          <motion.div className="p-8 space-y-4" variants={cardContentVariants}>
            <motion.div variants={cardItemVariants}>
              <h3 className="text-2xl font-bold text-white group-hover:text-blue-300 transition-colors mb-1">
                {project.title}
              </h3>
              <p className="text-white/50 text-sm">{project.subtitle}</p>
            </motion.div>

            <motion.p
              className="text-white/60 leading-relaxed text-sm"
              variants={cardItemVariants}
            >
              {project.description}
            </motion.p>

            <motion.div className="flex flex-wrap gap-2 pt-2" variants={tagVariants}>
              {project.tech.map((tech, idx) => (
                <motion.span
                  key={idx}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-white/70"
                  variants={cardItemVariants}
                  whileHover={{ y: -2 }}
                >
                  {tech}
                </motion.span>
              ))}
            </motion.div>

            <motion.div className="flex gap-3 pt-4" variants={cardItemVariants}>
              <motion.a
                href={project.liveUrl || undefined}
                target={project.liveUrl ? "_blank" : undefined}
                rel={project.liveUrl ? "noopener noreferrer" : undefined}
                className={`flex-1 ${buttonBaseClass} ${
                  project.liveUrl ? "" : disabledClass
                }`}
                aria-disabled={!project.liveUrl}
                title={
                  project.liveUrl
                    ? `${copy.viewLiveTitlePrefix} ${project.title}`
                    : copy.liveUnavailable
                }
                aria-label={
                  project.liveUrl
                    ? `${copy.viewLiveTitlePrefix} ${project.title}`
                    : copy.liveUnavailable
                }
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm font-medium">{copy.viewLive}</span>
              </motion.a>
              <motion.a
                href={project.repoUrl || undefined}
                target={project.repoUrl ? "_blank" : undefined}
                rel={project.repoUrl ? "noopener noreferrer" : undefined}
                className={`${buttonBaseClass} ${
                  project.repoUrl ? "" : disabledClass
                }`}
                aria-label={
                  project.repoUrl
                    ? `${copy.repoAriaPrefix} ${project.title}`
                    : copy.repoUnavailable
                }
                aria-disabled={!project.repoUrl}
                title={
                  project.repoUrl
                    ? `${copy.repoTitlePrefix} ${project.title}`
                    : copy.repoUnavailable
                }
                whileHover={{ y: -2, rotate: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <Github className="w-4 h-4" />
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.div>

        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </Card>
    </animated.div>
  );
};

const ProjectCardSkeleton = ({ copy }) => (
  <Card className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10">
    <div className="relative h-64 overflow-hidden">
      <Skeleton className="absolute inset-0 w-full h-full rounded-none bg-white/5" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/50">
        <Skeleton className="w-16 h-16 rounded-full bg-white/10" />
        <span className="text-xs uppercase tracking-wider">
          {copy.skeletonPreparing}
        </span>
      </div>
    </div>
    <div className="p-8 space-y-4">
      <Skeleton className="h-6 w-2/3 bg-white/10" />
      <Skeleton className="h-4 w-1/2 bg-white/5" />
      <Skeleton className="h-16 w-full bg-white/5" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-20 bg-white/5" />
        <Skeleton className="h-8 w-20 bg-white/5" />
        <Skeleton className="h-8 w-20 bg-white/5" />
      </div>
    </div>
  </Card>
);

export default Projects;
